import { Combobox, Transition } from "@headlessui/react";
import cx from "classnames";
import { Fragment, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Checkmark } from "@/components/icons/checkmark";
import { ExpandCollapseCaret } from "@/components/icons/expand-collapse-caret";
import { FormControl } from "@/components/shared/forms";
import type { LinkedEventOption } from "@/content-hub/linked-events/types";

import type { EventOptionsLoader } from "./use-event-options-loader";

export type EventOptionsComboboxProps = {
  dataTestId?: string;
  id: string;
  label: string;
  loader: EventOptionsLoader;
  name: string;
  onChange: (option: LinkedEventOption | null) => void;
  selectedOption: LinkedEventOption | null;
  showNoneOption?: boolean;
};

export function EventOptionsCombobox({
  dataTestId,
  id,
  label,
  loader,
  name,
  onChange,
  selectedOption,
  showNoneOption = true,
}: EventOptionsComboboxProps) {
  const { t } = useTranslation();
  const eventOptionsButtonRef = useRef<HTMLButtonElement>(null);
  const { options, pageInfo, status, setSearchQuery, loadMore } = loader;

  const handleInputClick = (open: boolean) => {
    if (open) return;

    eventOptionsButtonRef.current?.click();
  };

  return (
    <Combobox value={selectedOption} onChange={onChange} nullable>
      {({ open }) => (
        <div className="relative">
          <FormControl id={id} label={label}>
            <Combobox.Input
              id={id}
              name={name}
              data-testid={dataTestId}
              placeholder={t("content-hub.linked-event.choose-event")}
              autoComplete="off"
              className="mb-2 w-full truncate rounded-lg border border-solid border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-800 focus:outline-blue-500"
              displayValue={(eventOption: LinkedEventOption | null) =>
                eventOption?.name ?? ""
              }
              onChange={(event) => setSearchQuery(event.target.value)}
              onClick={() => handleInputClick(open)}
              onBlur={() => setSearchQuery("")}
            />
          </FormControl>
          <Combobox.Button
            ref={eventOptionsButtonRef}
            className="absolute right-2 top-[2.125rem] flex size-4 items-center justify-center text-gray-700"
          >
            <ExpandCollapseCaret
              className={cx("size-4 transition-all ease-in-out", {
                "rotate-180": open,
              })}
            />
          </Combobox.Button>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
              {showNoneOption && (
                <Combobox.Option
                  value={null}
                  className={({ active }) =>
                    cx(
                      "flex cursor-default select-none items-center justify-between px-3 py-2",
                      {
                        "bg-gray-100 text-gray-900": active,
                        "text-gray-900": !active,
                      }
                    )
                  }
                >
                  {({ selected }) => (
                    <>
                      <span>{t("content-hub.linked-event.none")}</span>
                      {selected ? <Checkmark className="size-4" /> : null}
                    </>
                  )}
                </Combobox.Option>
              )}
              {options.map((option) => (
                <Combobox.Option
                  key={option.id}
                  value={option}
                  className={({ active }) =>
                    cx(
                      "flex cursor-default select-none items-center justify-between gap-x-3 px-3 py-2",
                      {
                        "bg-gray-100 text-gray-900": active,
                        "text-gray-900": !active,
                      }
                    )
                  }
                >
                  {({ selected }) => (
                    <>
                      <span className="min-w-0 truncate">{option.name}</span>
                      {selected ? (
                        <Checkmark className="size-4 shrink-0" />
                      ) : null}
                    </>
                  )}
                </Combobox.Option>
              ))}
              {status === "processing" && (
                <div className="px-3 py-2 text-gray-600">
                  {t("content-hub.linked-event.loading-events")}
                </div>
              )}
              {status !== "processing" &&
                pageInfo.hasNextPage &&
                pageInfo.endCursor && (
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-blue-600 hover:bg-gray-100"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={loadMore}
                  >
                    {t("content-hub.linked-event.load-more-events")}
                  </button>
                )}
              {status !== "processing" && options.length === 0 && (
                <div className="px-3 py-2 text-gray-600">
                  {status === "failed"
                    ? t("content-hub.linked-event.failed-to-load")
                    : t("content-hub.linked-event.no-events")}
                </div>
              )}
            </Combobox.Options>
          </Transition>
        </div>
      )}
    </Combobox>
  );
}
